import React, { memo, useCallback } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { DataReplayNodeData } from '../../types';
import { History } from 'lucide-react';
import useStore from '../../store/useStore';
import { nodeColors } from '../../components/left-panel/constants';

const DataReplayNode = memo(({ data, selected, id }: NodeProps<DataReplayNodeData>) => {
  const { setSelectedNode } = useStore();

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNode({ id, type: 'data-replay', data, position: { x: 0, y: 0 } });
  }, [id, data, setSelectedNode]);

  return (
    <div
      onClick={handleClick} 
      className={`
        inline-block min-w-[128px] max-w-[320px] pl-3 pr-4 py-2 rounded-lg bg-white dark:bg-gray-700 border transition-all cursor-pointer shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark
          ${selected 
            ? "border-teal-500 shadow-teal-100 outline outline-4 outline-teal-500/20 outline-offset-0" 
            : "border-gray-300 dark:border-gray-600"
          }
      `}
    >
      <Handle 
        type="target" 
        position={Position.Left}
        className="!bg-teal-500 !border-2 !border-teal-300 !w-3 !h-3"
        style={{
          top: `calc(50% - 5px)`,
        }}
      />
      <div className="flex items-center gap-2 node-content w-full">
        <div className={`p-1.5 rounded ${selected ? 'bg-teal-200' : nodeColors["data-replay"].bg}`}>
          <History className={`w-4 h-4 ${selected ? 'text-teal-600' : nodeColors["data-replay"].text}`} />
        </div>
        <div className="min-w-0 content-container w-full">
          {/* Name - only show if visibility is not hidden */}
          {data.nameVisibility !== "hidden" && (
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate w-full">
              {data.name || 'Data Replay'}
            </h3>
          )}
          
          <div className="flex flex-wrap gap-1">
            {/* Replay Source - show if we have one */}
            {data.replaySource && data.replaySource.trim() !== "" && data.replayVisibility !== "hidden" && (
              <span
                className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate max-w-full bg-teal-100 text-teal-800"
              >
                {data.replaySource}
              </span>
            )}
          </div>
          
        </div>
      </div>
      {/* Notes - only show if visibility is not hidden and we have notes */}
      {data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden" && (
        <div className="mt-2 text-[8px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded max-h-[80px] overflow-y-auto whitespace-pre-line">
          {data.notes}
        </div>
      )}
    </div>
  );
});

DataReplayNode.displayName = 'DataReplayNode';

export default DataReplayNode;
